<script setup>
import AdminLayout from "../Layouts/AdminLayout.vue";
import Button from "primevue/button";
import Tag from "primevue/tag";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import { Link } from "@inertiajs/inertia-vue3";

const props = defineProps({
    notification: {
        type: Object,
        required: true
    }
});

const getTypeSeverity = (type) => {
    switch(type) {
        case 'all': return 'info';
        case 'all_active': return 'info';
        case 'many': return 'warning';
        case 'customer': return 'success';
        default: return 'secondary';
    }
};

const getStatusSeverity = (status) => {
    switch(status) {
        case 'processing': return 'warning';
        case 'sent': return 'success';
        case 'failed': return 'danger';
        default: return 'secondary';
    }
};

const getStatusLabel = (status) => {
    switch(status) {
        case 'processing': return 'Processing';
        case 'sent': return 'Sent';
        case 'failed': return 'Failed';
        default: return status;
    }
};

const isAudio = (mime) => {
    return mime && mime.startsWith('audio/');
};

const isImage = (mime) => {
    return mime && mime.startsWith('image/');
};

const isVideo = (mime) => {
    return mime && mime.startsWith('video/');
};

const getFileTypeLabel = (mime) => {
    if (!mime) return 'File';
    
    if (mime.includes('pdf')) return 'PDF Document';
    if (mime.includes('word') || mime.includes('document')) return 'Word Document';
    if (mime.includes('sheet') || mime.includes('excel')) return 'Spreadsheet';
    if (mime.includes('powerpoint') || mime.includes('presentation')) return 'Presentation';
    if (mime.includes('text')) return 'Text File';
    if (mime.includes('zip') || mime.includes('rar') || mime.includes('7z')) return 'Archive';
    
    return 'File';
};

const renderMarkdown = (content) => {
    if (!content) return '';
    
    // Convert markdown to HTML
    let html = content
        // Convert bold text
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        // Convert italic text
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        // Convert links
        .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
        // Convert bullet lists
        .replace(/^\- (.+)$/gm, '<li>$1</li>')
        // Wrap consecutive li elements in ul
        .replace(/(<li>.*<\/li>\s*)+/g, (match) => `<ul>${match}</ul>`)
        // Convert line breaks to <br> tags
        .replace(/\n/g, '<br>')
        // Preserve whitespace
        .replace(/  /g, '&nbsp;&nbsp;');
    
    return html;
};
</script>

<template>
    <AdminLayout>
        <div class="p-6">
            <div class="flex justify-between items-center mb-6">
                <h1 class="text-2xl font-semibold">Notification Details</h1>
                <Link 
                    href="/admin/notifications/history" 
                    class="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex items-center gap-2"
                >
                    <i class="pi pi-arrow-left"></i>
                    Back to History
                </Link>
            </div>

            <div class="bg-white rounded-lg shadow">
                <div class="p-6 space-y-6">
                    <!-- Header Info -->
                    <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                            <label class="text-sm text-gray-500">Title</label>
                            <p class="font-semibold">{{ notification.title }}</p>
                        </div>
                        <div>
                            <label class="text-sm text-gray-500">Type</label>
                            <div class="mt-1">
                                <Tag :value="notification.type" :severity="getTypeSeverity(notification.type)" />
                            </div>
                        </div>
                        <div>
                            <label class="text-sm text-gray-500">Status</label>
                            <div class="mt-1">
                                <Tag :value="getStatusLabel(notification.status)" :severity="getStatusSeverity(notification.status)" />
                            </div>
                        </div>
                        <div>
                            <label class="text-sm text-gray-500">Sent At</label>
                            <p>{{ notification.created_at }}</p>
                            <p class="text-sm text-gray-500">{{ notification.created_at_human }}</p>
                        </div>
                    </div>

                    <!-- Summary -->
                    <div>
                        <label class="text-sm text-gray-500">Summary</label>
                        <p class="mt-1">{{ notification.body }}</p>
                    </div>

                    <!-- Content -->
                    <div>
                        <label class="text-sm text-gray-500">Content</label>
                        <div class="mt-2 p-4 bg-gray-50 rounded-lg">
                            <div v-html="renderMarkdown(notification.content)" class="prose prose-sm max-w-none"></div>
                        </div>
                    </div>

                    <!-- Recipients -->
                    <div v-if="notification.type === 'customer' || notification.type === 'many'">
                        <label class="text-sm text-gray-500">Recipients ({{ notification.recipients.length }})</label>
                        <div class="mt-2">
                            <DataTable 
                                :value="notification.recipients" 
                                :paginator="notification.recipients.length > 10"
                                :rows="10"
                                responsiveLayout="scroll"
                                class="p-datatable-sm"
                            >
                                <Column field="name" header="Name" />
                                <Column field="email" header="Email" />
                            </DataTable>
                        </div>
                    </div>
                    
                    <div v-else-if="notification.type === 'all' || notification.type === 'all_active'">
                        <label class="text-sm text-gray-500">Recipients</label>
                        <p class="mt-1 font-medium">
                            {{ notification.type === 'all_active' ? 'All Active Customers' : 'All Customers' }}
                            <span class="text-sm text-gray-500">({{ notification.customer_count }} total)</span>
                        </p>
                    </div>

                    <!-- Attachments -->
                    <div v-if="notification.attachments && notification.attachments.length > 0">
                        <label class="text-sm text-gray-500">Attachments ({{ notification.attachments.length }})</label>
                        <div class="mt-2 space-y-3">
                            <div 
                                v-for="attachment in notification.attachments" 
                                :key="attachment.id"
                                class="border rounded-lg p-4 bg-gray-50"
                            >
                                <!-- Audio attachments -->
                                <div v-if="isAudio(attachment.mime)" class="space-y-2">
                                    <div class="flex items-center justify-between">
                                        <div class="flex items-center gap-2">
                                            <i class="pi pi-volume-up text-xl text-gray-600"></i>
                                            <span class="font-medium">Audio File</span>
                                            <span class="text-sm text-gray-500">({{ attachment.size_human }})</span>
                                        </div>
                                        <a 
                                            :href="attachment.url" 
                                            target="_blank"
                                            class="text-blue-600 hover:text-blue-800 text-sm"
                                        >
                                            <i class="pi pi-download mr-1"></i>
                                            Download
                                        </a>
                                    </div>
                                    <audio 
                                        controls 
                                        class="w-full"
                                        :src="attachment.url"
                                    >
                                        Your browser does not support the audio element.
                                    </audio>
                                </div>
                                
                                <!-- Image attachments -->
                                <div v-else-if="isImage(attachment.mime)" class="space-y-2">
                                    <div class="flex items-center justify-between mb-2">
                                        <div class="flex items-center gap-2">
                                            <i class="pi pi-image text-xl text-gray-600"></i>
                                            <span class="font-medium">Image</span>
                                            <span class="text-sm text-gray-500">({{ attachment.size_human }})</span>
                                        </div>
                                        <a 
                                            :href="attachment.url" 
                                            target="_blank"
                                            class="text-blue-600 hover:text-blue-800 text-sm"
                                        >
                                            <i class="pi pi-external-link mr-1"></i>
                                            View Full Size
                                        </a>
                                    </div>
                                    <img 
                                        :src="attachment.url" 
                                        :alt="`Attachment ${attachment.id}`"
                                        class="max-w-full h-auto rounded border"
                                        style="max-height: 400px;"
                                    />
                                </div>
                                
                                <!-- Video attachments -->
                                <div v-else-if="isVideo(attachment.mime)" class="space-y-2">
                                    <div class="flex items-center justify-between mb-2">
                                        <div class="flex items-center gap-2">
                                            <i class="pi pi-video text-xl text-gray-600"></i>
                                            <span class="font-medium">Video</span>
                                            <span class="text-sm text-gray-500">({{ attachment.size_human }})</span>
                                        </div>
                                        <a 
                                            :href="attachment.url" 
                                            target="_blank"
                                            class="text-blue-600 hover:text-blue-800 text-sm"
                                        >
                                            <i class="pi pi-download mr-1"></i>
                                            Download
                                        </a>
                                    </div>
                                    <video 
                                        controls 
                                        class="w-full rounded"
                                        style="max-height: 400px;"
                                    >
                                        <source :src="attachment.url" :type="attachment.mime">
                                        Your browser does not support the video tag.
                                    </video>
                                </div>
                                
                                <!-- Other file types -->
                                <div v-else class="flex items-center justify-between">
                                    <div class="flex items-center gap-2">
                                        <i class="pi pi-file text-xl text-gray-600"></i>
                                        <span class="font-medium">{{ getFileTypeLabel(attachment.mime) }}</span>
                                        <span class="text-sm text-gray-500">({{ attachment.size_human }})</span>
                                    </div>
                                    <a 
                                        :href="attachment.url" 
                                        target="_blank"
                                        class="text-blue-600 hover:text-blue-800 text-sm"
                                    >
                                        <i class="pi pi-download mr-1"></i>
                                        Download
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </AdminLayout>
</template>

<style scoped>
/* Enhanced styling for rich text content */
.prose {
    font-size: 0.875rem;
    line-height: 1.75;
    color: #374151;
    white-space: pre-wrap;
    word-wrap: break-word;
    overflow-wrap: break-word;
}

.prose :deep(p) {
    margin-bottom: 1rem;
}

.prose :deep(strong) {
    font-weight: 600;
    color: #111827;
}

.prose :deep(em) {
    font-style: italic;
}

.prose :deep(ul) {
    list-style-type: disc;
    margin-left: 1.5rem;
    margin-bottom: 1rem;
    margin-top: 0.5rem;
}

.prose :deep(ol) {
    list-style-type: decimal;
    margin-left: 1.5rem;
    margin-bottom: 1rem;
    margin-top: 0.5rem;
}

.prose :deep(li) {
    margin-bottom: 0.25rem;
}

.prose :deep(a) {
    color: #3b82f6;
    text-decoration: underline;
    word-break: break-word;
}

.prose :deep(a:hover) {
    color: #2563eb;
}

.prose :deep(br) {
    content: "";
    display: block;
    margin-bottom: 0.5rem;
}

/* Audio player styling */
audio {
    height: 40px;
    border-radius: 25px;
    outline: none;
}

audio::-webkit-media-controls-panel {
    background-color: #f3f4f6;
}

/* Style for modern browsers */
audio::-webkit-media-controls-play-button,
audio::-webkit-media-controls-mute-button {
    filter: invert(0.3);
}

/* Video player styling */
video {
    background-color: #000;
}
</style>